import type { ToggleOption } from '@/components'
import {
  Button,
  CenteredLoader,
  FAIcon,
  ModuleWrapper,
  Toggles,
} from '@/components'
import { FullPageLoader } from '@/components_deprecated'
import { PageType } from '@/modules/common/consts'
import { Module } from '@/modules/companies'
import {
  asBoolean,
  asDateOrDefault,
  asEnumValue,
  asEnumValueOrEmpty,
  asNullableString,
  parseDateRangeOrDefault,
  asString,
  asStringArray,
  count,
} from '@/utils'
import { endOfWeek, startOfWeek } from '@/utils/date-fns'
import {
  useAdmin,
  useAutoSelectSingleLeaderGroup,
  useLeaderGroupsSuspense,
  useNodes,
  useOnErrorAuto,
  useParseQueryState,
  usePermissions,
  useQueryFieldValue,
  useUser,
} from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { useMutation, useSuspenseQuery } from '@apollo/client'
import { useForm as useFormOld, useTranslate } from '@ur-solutions/react-hooks'
import { addMonths, endOfMonth, startOfMonth } from 'date-fns'
import type { VariablesOf } from 'gql.tada'
import { Activity, Suspense, useEffect, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useNavigate } from 'react-router'
import {
  AbsenceDialog,
  AbsenceRequests,
  AbsenceRequestsGantt,
  AbsencesTable,
} from './components'
import { AbsenceLockDialog } from './components/absence-lock-dialog'
import {
  AbsencesFilters,
  AbsencesFiltersSchema,
} from './components/absences-filters'
import { AbsencesCalendar } from './components/AbsencesCalendar'
import { useGetAbsenceLockState } from './components/handler.hooks'
import { UNLOCK_ABSENCES_MUTATION } from './mutations'
import {
  ACTIVE_ABSENCE_LOCK_QUERY,
  PENDING_ABSENCE_COUNT_QUERY,
  USER_ABSENCES_QUERY,
} from './queries'
import { absenceRequestStatuses } from './absence-status.mapping'
import { absenceViewValues } from './consts'
import type { AbsenceFilterForm } from './types'
import type { AbsenceTypeNode } from './types.graphql'
import { getCalendarAbsenceQueryRange, useAbsenceLockedBefore } from './util'
import { GripIcon } from 'lucide-react'

function useInitialFilters(): AbsenceFilterForm {
  const me = useUser()
  const { hasPermissions } = usePermissions()

  const parsed = useParseQueryState(AbsencesFiltersSchema, 'view')

  const dateStart = startOfMonth(addMonths(new Date(), -1))
  const dateEnd = endOfMonth(new Date())

  return {
    view: parsed.view ?? 'table',
    users: hasPermissions(PERMISSIONS.timesheets.view.othersabsence)
      ? []
      : [me.id],
    absenceType: '',
    requestStatus: '',
    dateRange: {
      from: dateStart,
      to: dateEnd,
    },
    byCreatedDate: false,
    month: new Date(),
    leaderGroup: undefined,
    orderBy: 'start',
  }
}

function useAbsencesQueryVariables(
  filter: AbsenceFilterForm
): VariablesOf<typeof USER_ABSENCES_QUERY> {
  const { hasPermissions } = usePermissions()
  const canViewOthersAbsence = hasPermissions(
    PERMISSIONS.timesheets.view.othersabsence,
    false
  )

  const users = filter.users
  const absenceType = filter.absenceType || undefined
  const requestStatus = filter.requestStatus || undefined
  const formLeaderGroup = filter.leaderGroup || undefined
  const leaderGroup = canViewOthersAbsence ? formLeaderGroup : undefined
  const byCreatedDate = filter.byCreatedDate

  const { from, to } = filter.dateRange

  const variables: VariablesOf<typeof USER_ABSENCES_QUERY> = {
    users,
    absenceType,
    requestStatus,
    leaderGroup,
  }

  const start: string | undefined = from?.toISOString()
  const end: string | undefined = to?.toISOString()

  if (filter.view === 'calendar') {
    Object.assign(variables, getCalendarAbsenceQueryRange(filter.month))
  } else if (byCreatedDate) {
    variables.createdAtGte = start
    variables.createdAtLte = end
  } else {
    variables.datetimeStartGte = start
    variables.datetimeStartLte = end
  }

  return variables
}

interface AbsencesContentProps {
  filter: AbsenceFilterForm
  createLockedBefore: Date | null
  onOrderByChange(orderBy: string): void
  leaderGroups: ReturnType<typeof useLeaderGroupsSuspense>
  canEditForOthers: boolean
}

function AbsencesContent({
  filter,
  createLockedBefore,
  onOrderByChange,
  leaderGroups,
  canEditForOthers,
}: AbsencesContentProps) {
  const [selectedAbsenceType, setSelectedAbsenceType] = useState<Pick<
    AbsenceTypeNode,
    'id' | 'name'
  > | null>(null)

  const filterView = filter.view
  const filterMonth = filter.month

  const { datetimeEndGte, datetimeStartLt } = (() => {
    const startOfMonthDate = startOfMonth(filterMonth)
    const endOfMonthDate = addMonths(startOfMonthDate, 1)

    const datetimeStartLt =
      filterView === 'table' ? startOfMonthDate : startOfWeek(startOfMonthDate)
    const datetimeEndGte =
      filterView === 'table' ? endOfMonthDate : endOfWeek(endOfMonthDate)

    return {
      datetimeStartLt,
      datetimeEndGte,
    }
  })()

  const queryVariables = useAbsencesQueryVariables(filter)

  const onErrorAuto = useOnErrorAuto()
  const { data: absencesData } = useSuspenseQuery(USER_ABSENCES_QUERY, {
    skip: filterView === 'requests' || filterView === 'gantt',
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
    variables: queryVariables,
    onError: onErrorAuto(),
  })
  const absences = useNodes(absencesData?.allAbsences)

  return (
    <>
      <Activity mode={filterView === 'table' ? 'visible' : 'hidden'}>
        <AbsencesTable
          absences={absences}
          createLockedBefore={createLockedBefore}
          selectedAbsenceType={selectedAbsenceType}
          initialOrderBy={
            filter.orderBy === 'start' ? undefined : filter.orderBy
          }
          onOrderByChange={onOrderByChange}
          onSelectAbsenceType={absenceType => {
            setSelectedAbsenceType(absenceType)
          }}
        />
      </Activity>

      <Activity mode={filterView === 'calendar' ? 'visible' : 'hidden'}>
        <AbsencesCalendar
          startDate={datetimeStartLt}
          endDate={datetimeEndGte}
          absences={absences}
        />
      </Activity>

      <Activity mode={filterView === 'requests' ? 'visible' : 'hidden'}>
        <AbsenceRequests filter={filter} />
      </Activity>

      <Activity mode={filterView === 'gantt' ? 'visible' : 'hidden'}>
        <AbsenceRequestsGantt
          filter={filter}
          leaderGroups={leaderGroups}
          canEditForOthers={canEditForOthers}
          createLockedBefore={createLockedBefore}
        />
      </Activity>
    </>
  )
}

function AbsencesInner() {
  const translations = useTranslate({
    absences: 'common.absences',
  })

  const me = useUser()
  const isAdmin = useAdmin()
  const navigate = useNavigate()
  const { hasPermissions } = usePermissions()

  const canManageRequests = hasPermissions(
    PERMISSIONS.timesheets.change.othersabsence
  )
  const canViewOthersAbsence = hasPermissions(
    PERMISSIONS.timesheets.view.othersabsence,
    false
  )
  const canAddAbsence = hasPermissions(PERMISSIONS.timesheets.add.absence)
  const canAddOthersAbsence = hasPermissions(
    PERMISSIONS.timesheets.add.othersabsence,
    false
  )
  const canManageLocks = hasPermissions(
    PERMISSIONS.timesheets.add.absencelockconfig
  )

  const getAbsenceLockState = useGetAbsenceLockState()

  const leaderGroups = useLeaderGroupsSuspense(Module.ABSENCES)

  const {
    formValues: filter,
    updateForm,
    loadStoredFormState,
  } = useFormOld<AbsenceFilterForm>({
    values: useInitialFilters(),
    config: {
      storage: {
        storeFormState: true,
        storeFormStateName: 'absence-filters',
        sessionStorage: true,
        retrieveFormStateOnMount: true,
        transformValues: {
          view: value => asEnumValue(value, absenceViewValues),
          users: value =>
            canViewOthersAbsence ? asStringArray(value) : [me.id],
          absenceType: asString,
          requestStatus: value =>
            asEnumValueOrEmpty(value, absenceRequestStatuses),
          byCreatedDate: asBoolean,
          month: value => asDateOrDefault(value, new Date()),
          leaderGroup: asNullableString,
          orderBy: asString,
          dateRange: parseDateRangeOrDefault,
        },
      },
    },
  })

  const onErrorAuto = useOnErrorAuto()

  const { data: absenceLockData } = useSuspenseQuery(
    ACTIVE_ABSENCE_LOCK_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-and-network',
      onError: onErrorAuto(),
    }
  )
  const { createLockedBefore } = useAbsenceLockedBefore()
  const activeLock = absenceLockData?.activeAbsenceLock ?? null

  const { data: pendingCountData } = useSuspenseQuery(
    PENDING_ABSENCE_COUNT_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-and-network',
    }
  )

  const [unlockAbsences, { loading: unlockAbsencesLoading }] = useMutation(
    UNLOCK_ABSENCES_MUTATION,
    {
      refetchQueries: ['ActiveAbsenceLock'],
      awaitRefetchQueries: true,
    }
  )

  const viewToggleOptions: ToggleOption<AbsenceFilterForm['view']>[] = [
    {
      value: 'table',
      label: <FAIcon icon="list" />,
    },
    {
      value: 'requests',
      label: <FAIcon icon="memo-circle-check" />,
      hide: !canManageRequests,
      badge: isMobile ? null : pendingCountData?.pendingAbsenceCount || null,
    },
    {
      value: 'calendar',
      label: <FAIcon icon="calendar-alt" />,
      hide: isMobile,
    },
    {
      value: 'gantt',
      label: <GripIcon size={16} />,
      hide: !canManageRequests || isMobile,
    },
  ]

  const showViewToggle = count(viewToggleOptions, opt => !opt.hide) > 1

  useQueryFieldValue('view', filter.view)

  useAutoSelectSingleLeaderGroup({
    canViewOthers: canViewOthersAbsence,
    isAdmin,
    leaderGroup: filter.leaderGroup,
    leaderGroups: leaderGroups.myLeaderGroups,
    loadStoredFormState,
    updateForm,
    onAutoSelect: group => {
      updateForm({ leaderGroup: group.id })
    },
  })

  // On small screens, default to requests view if user can manage requests, otherwise table view
  // Gantt view is not suitable for small screens, so we switch to requests or table view when user tries to access it on mobile
  useEffect(() => {
    if (isMobile && filter.view === 'gantt') {
      updateForm({ view: canManageRequests ? 'requests' : 'table' })
    }
  }, [canManageRequests, filter.view, updateForm])

  const { createLocked } = getAbsenceLockState(activeLock)

  return (
    <ModuleWrapper
      title={translations.absences}
      page={PageType.ABSENCES}
      headerActions={
        !createLocked &&
        canAddAbsence && (
          <AbsenceDialog
            canAddForOthers={canAddOthersAbsence}
            createLockedBefore={createLockedBefore}
          />
        )
      }
      headerRight={
        <div className="flex items-center gap-2">
          {canManageLocks && (
            <div className="flex">
              {!!activeLock && (
                <Button
                  className="rounded-r-none border-r-[0.5px]"
                  variant="outline"
                  size="icon"
                  disabled={unlockAbsencesLoading}
                  onClick={() => unlockAbsences()}
                >
                  <FAIcon className="text-xl" icon="lock-open-alt" />
                </Button>
              )}

              <AbsenceLockDialog
                activeLock={activeLock}
                onCreateConfig={id =>
                  void navigate(`/absences/edit-lock/${id ?? ''}`)
                }
              />
            </div>
          )}

          <Activity mode={showViewToggle ? 'visible' : 'hidden'}>
            <Toggles
              value={filter.view}
              options={viewToggleOptions}
              variant="white"
              size={isMobile ? 'md' : 'lg'}
              onChange={value =>
                updateForm({ view: value as AbsenceFilterForm['view'] })
              }
            />
          </Activity>
        </div>
      }
    >
      <AbsencesFilters
        filter={filter}
        updateForm={updateForm}
        leaderGroups={leaderGroups}
      />

      <Suspense fallback={<CenteredLoader />}>
        <AbsencesContent
          filter={filter}
          createLockedBefore={createLockedBefore}
          leaderGroups={leaderGroups}
          canEditForOthers={canManageRequests}
          onOrderByChange={orderBy => updateForm({ orderBy })}
        />
      </Suspense>
    </ModuleWrapper>
  )
}

export function Absences() {
  return (
    <Suspense fallback={<FullPageLoader />}>
      <AbsencesInner />
    </Suspense>
  )
}
